import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Checkbox,
  IconButton,
  Rating,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  useTheme,
  CircularProgress
} from '@mui/material';
import { CheckCircle as PublishIcon, Cancel as UnpublishIcon, Delete as DeleteIcon } from '@mui/icons-material';
import reviewsService from '../../services/reviewsService';
import adminService from '../../services/adminService';
import { ButtonPill, panelSx } from '../ui';

const ReviewsManagement = () => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [publishedFilter] = useState('');
  const [selected, setSelected] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const filters = { page: page + 1, per_page: rowsPerPage };
      if (publishedFilter !== '') filters.published = publishedFilter;
      const res = await reviewsService.getAllReviews(filters);
      const list = res?.reviews ?? [];
      setReviews(list);
      setTotalCount(res?.pagination?.total_count ?? res?.pagination?.total ?? list.length);
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setReviews([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
    setSelected([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, publishedFilter]);

  const handlePublish = async (reviewId) => {
    try {
      await reviewsService.publishReview(reviewId);
      setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, is_published: !r.is_published } : r));
    } catch (err) {
      console.error('Publish failed:', err);
    }
  };

  const handleDelete = async (reviewId) => {
    try {
      await reviewsService.deleteReview(reviewId);
      setReviews(prev => prev.filter(r => r.id !== reviewId));
      setTotalCount(prev => Math.max(0, prev - 1));
      setSelected(prev => prev.filter(id => id !== reviewId));
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(reviews.map(r => r.id));
    } else {
      setSelected([]);
    }
  };

  const runBulk = async (action) => {
    if (selected.length === 0) return;
    try {
      setBulkLoading(true);
      const res = await adminService.bulkAction('reviews', { ids: selected, action });
      const verb = action === 'delete' ? 'deleted' : action === 'publish' ? 'published' : 'unpublished';
      setSnackbar({ open: true, message: `${res.affected ?? selected.length} review(s) ${verb}`, severity: 'success' });
      setSelected([]);
      await fetchReviews();
    } catch (err) {
      setSnackbar({ open: true, message: err?.message ?? 'Bulk action failed', severity: 'error' });
    } finally {
      setBulkLoading(false);
      setConfirmDeleteOpen(false);
    }
  };

  const allSelected = reviews.length > 0 && selected.length === reviews.length;
  const someSelected = selected.length > 0 && selected.length < reviews.length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Reviews Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Moderate and manage customer reviews
          </Typography>
        </Box>
        <Box>
          <Chip
            label={`Published: ${reviews.filter(r => r.is_published ?? r.isPublished).length}`}
            color="success"
            sx={{ mr: 1 }}
          />
          <Chip
            label={`Pending: ${reviews.filter(r => !(r.is_published ?? r.isPublished)).length}`}
            color="warning"
          />
        </Box>
      </Box>

      {selected.length > 0 && (
        <Paper sx={{ ...panelSx(theme), mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {selected.length} selected
          </Typography>
          <Box sx={{ flex: 1 }} />
          <ButtonPill
            size="small"
            color="success"
            startIcon={<PublishIcon />}
            disabled={bulkLoading}
            onClick={() => runBulk('publish')}
          >
            Publish
          </ButtonPill>
          <ButtonPill
            size="small"
            variant="outlined"
            startIcon={<UnpublishIcon />}
            disabled={bulkLoading}
            onClick={() => runBulk('unpublish')}
          >
            Unpublish
          </ButtonPill>
          <ButtonPill
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={bulkLoading}
            onClick={() => setConfirmDeleteOpen(true)}
          >
            Delete
          </ButtonPill>
        </Paper>
      )}

      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleSelectAll}
                  inputProps={{ 'aria-label': 'select all reviews' }}
                />
              </TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Trip</TableCell>
              <TableCell align="center">Rating</TableCell>
              <TableCell>Review</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reviews.map((review) => {
              const pub = review.is_published ?? review.isPublished;
              const name = review.reviewer_name ?? review.user?.firstName ?? (review.user_initials || '?');
              const tripTitle = review.trip?.title ?? '-';
              const isSelected = selected.includes(review.id);
              return (
              <TableRow key={review.id} hover selected={isSelected}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    onChange={() => toggleSelect(review.id)}
                    inputProps={{ 'aria-label': `select review ${review.id}` }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                      {(review.user_initials || name || '?').charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {name}
                      </Typography>
                      {(review.is_verified ?? review.isVerified) && (
                        <Chip label="Verified" size="small" color="success" sx={{ height: 16 }} />
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {tripTitle}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Rating value={review.rating} size="small" readOnly />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {review.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300, display: 'block' }}>
                    {review.comment}
                  </Typography>
                </TableCell>
                <TableCell>
                  {(review.created_at || review.createdAt) ? new Date(review.created_at || review.createdAt).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={pub ? 'Published' : 'Pending'}
                    color={pub ? 'success' : 'warning'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  {!pub && (
                    <IconButton size="small" color="success" onClick={() => handlePublish(review.id)}>
                      <PublishIcon />
                    </IconButton>
                  )}
                  <IconButton size="small" color="error" onClick={() => handleDelete(review.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
            })}
          </TableBody>
        </Table>
        )}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
        />
      </TableContainer>

      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Delete reviews</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete {selected.length} selected review(s)? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={bulkLoading}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => runBulk('delete')} disabled={bulkLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReviewsManagement;
